import { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import handPhone from "@/assets/hand-phone.png";

import useMapStore from "@/store/mapStore";
import { t } from "i18next";
import { cn } from "@/lib/utils";

function QRCode({
  size,
  className,
  type = "full",
}: {
  size?: number;
  className?: string;
  type?: "base" | "full";
}) {
  const divRef = useRef<HTMLDivElement | null>(null);
  const [isHidden, setIsHidden] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState("");

  const currentLanguage = useMapStore((state) => state.currentLang);
  const haveRouteDetails = useMapStore((state) => state.haveRouteDetails);
  const haveRoutePreview = useMapStore((state) => state.haveRoutePreview);
  const routeStart = useMapStore((state) => state.routeStart);
  const routeFinish = useMapStore((state) => state.routeFinish);
  const navMode = useMapStore((state) => state.navMode);
  const cityStart = useMapStore((state) => state.cityStart);
  const cityFinish = useMapStore((state) => state.cityFinish);
  const activeCategory = useMapStore((state) => state.activeCategory);
  const verticalKioskMode = useMapStore((state) => state.verticalKioskMode);
  const kioskScaling = useMapStore((state) => state.kioskScaling);

  useEffect(() => {
    const url = new URL(window.location.href);
    const urlParams = new URLSearchParams(url.search);
    const newParams = new URLSearchParams();
    const originalHref = `${url.href}`;

    // Iterate over the search parameters and delete any that contain 'com.broadsign'
    for (const [key, value] of urlParams.entries()) {
      if (!key.includes("com.broadsign") && key !== "kiosk") {
        newParams.append(key, value);
      }
    }

    // Mall url
    // url.href = "https://www.malloftheemirates.com/en/map";

    newParams.set("qrCode", "true");
    newParams.set("language", currentLanguage);

    if (!routeStart && routeFinish.id) {
      newParams.set(
        "remote_id",
        // "destId",
        routeFinish.properties.remote_id
          ? routeFinish.properties.remote_id
          : routeFinish.id,
      );
    }
    if (routeFinish.id && routeStart) {
      newParams.set(
        "remote_id",
        routeFinish.properties.remote_id
          ? routeFinish.properties.remote_id
          : routeFinish.id,
      );
      if (routeStart !== "kiosk" && routeStart.properties) {
        url.href = originalHref;
        //newParams.set("qrCode", "true");
        //newParams.set("language", currentLanguage);
        newParams.set("startFeature", routeStart.properties.id);
      }
    }

    if (navMode !== "mall") {
      newParams.set("navMode", navMode);
      if (!cityStart.poiId) {
        newParams.set("cityStart", JSON.stringify(cityStart));
      } else {
        newParams.set("startFeature", cityStart.poiId);
      }
      if (!cityFinish.poiId)
        newParams.set("cityFinish", JSON.stringify(cityFinish));
    }

    url.search = newParams.toString();
    if (type === "base") {
      console.log("base", newParams.get("kiosk"));
      setQrCodeUrl(
        `${url.origin}/${url.pathname}?qrCode=true${newParams.get("kiosk") ? `&kiosk=${newParams.get("kiosk")}` : ""}&language=${currentLanguage}`,
      );
    } else {
      setQrCodeUrl(url.href);
    }
  }, [
    haveRouteDetails,
    haveRoutePreview,
    routeFinish,
    routeStart,
    currentLanguage,
    navMode,
    cityStart,
    cityFinish,
    type,
  ]);

  // Function to check visibility
  const checkVisibility = () => {
    if (verticalKioskMode) {
      return;
    }
    const element = divRef.current;
    const parent = element?.parentElement;

    if (element && element.getBoundingClientRect().height > 0 && parent) {
      const { top, bottom, height } = element.getBoundingClientRect();
      const parentRect = parent.getBoundingClientRect();

      // Calculate if the element is within the visible area of the parent
      const isFullyVisible =
        bottom > parentRect.top &&
        top < parentRect.bottom &&
        top + height > parentRect.top &&
        bottom < parentRect.bottom;

      setIsHidden(!isFullyVisible); // Set hidden state based on visibility
    } else if (parent) {
      setIsHidden(false);
    }
  };

  useEffect(() => {
    const parent = divRef.current?.parentElement;

    // Add scroll event listener
    const handleScroll = () => checkVisibility();
    parent?.addEventListener("scroll", handleScroll);

    // Add resize observer
    const resizeObserver = new ResizeObserver(() => checkVisibility());
    if (parent) {
      resizeObserver.observe(parent);
    }

    // Initial check
    checkVisibility();

    // Cleanup
    return () => {
      parent?.removeEventListener("scroll", handleScroll);
      resizeObserver.disconnect();
    };
  }, []); // Only set up listeners once, after the first render

  // Recheck visibility when `activeCategory` changes
  useEffect(() => {
    setTimeout(() => {
      checkVisibility();
    }, 300);
  }, [activeCategory]); // Dependency on activeCategory to trigger visibility check when it changes

  return (
    <div
      className={cn(
        "scale mb-10 mt-auto flex items-center justify-center rounded-xl",
        isHidden && "hidden",
        type === "base" &&
          "w-[460px] bg-white p-5 shadow-[0px_1px_6px_3px_rgba(0,0,0,0.5)]",
        !kioskScaling && "kiosk:origin-top-left kiosk:scale-150",
        className,
      )}
      ref={divRef}
    >
      <QRCodeSVG
        value={qrCodeUrl}
        className="shrink-0"
        size={size ? size : 96}
        level="H"
      />
      <p className="tracking-[-1px] text-foreground group-data-[dir=ltr]:pl-4 group-data-[dir=rtl]:pr-4">
        {t("scan-qr-code")}
      </p>
      <img src={handPhone} alt={t("scan-qr-code")} className="-mb-3 w-12" />
    </div>
  );
}

export default QRCode;
